import { Box, Button } from "@mui/material";
import Calendar from "components/elements/calendar/Calendar";
import AddIcon from "@mui/icons-material/Add";
import EventsForm from "./EventsForm";
import { useEffect, useState } from "react";
import {
  createEvent,
  fetchAcademicyears,
  fetchClassSections,
  getUsers,
  fetchEvents,
  fetchEventTypes,
  updateEvent,
  deleteEvent,
} from "ServerAPI/serverAPI";
import { useCookies } from "react-cookie";
import { Spinner } from "react-bootstrap";
import RefreshButton from "components/elements/refresh-button/RefreshButton";
import { useToast } from "context/toast-context/ToastProvider";
import ToastMessage from "components/Dialog/ToastMessage";
import EventInfo from "./EventInfo";
import { set } from "lodash";
import DeleteModalConfirmation from "components/Dialog/DeleteModalConfirmation";

const getRandomColor = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

const DAY_NAME_TO_INDEX = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

const addYears = (date, years) => {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() + years);
  return d;
};

const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const transformEvents = (serverEvents) => {
  const allEvents = [];

  for (const event of serverEvents) {
    const eventStartDate = new Date(event.startDate);
    const color = getRandomColor() ?? "#1976d2";

    const createEventInstance = (date) => {
      const startDateStr = toLocalDateStr(date);
      const base = {
        id: `${event.eventId}-${startDateStr}`,
        title: event.title,
        color,
        extendedProps: { ...event },
      };

      return event.allDay
        ? { ...base, start: startDateStr, allDay: true }
        : {
            ...base,
            start: `${startDateStr}T${event.startTime}:00`,
            end: `${startDateStr}T${event.endTime}:00`,
          };
    };

    const { recurrence } = event;
    const frequency = recurrence?.frequency;

    // Shared helper — use recurrenceEndDate from server if present, else cap at +1 year
    const getEndDateLimit = () => {
      const raw = recurrence?.recurrenceEndDate;
      if (raw) {
        const d = new Date(raw.split("T")[0]);
        if (!isNaN(d)) return d;
      }
      return addYears(eventStartDate, 1);
    };

    if (frequency === "weekly") {
      const days = recurrence.days_of_week ?? [];
      const endDateLimit = getEndDateLimit(); // ← uses recurrenceEndDate or +1yr
      const selectedDayIndices = new Set(days.map((d) => DAY_NAME_TO_INDEX[d]));
      const current = new Date(eventStartDate);

      while (current <= endDateLimit) {
        if (selectedDayIndices.has(current.getDay())) {
          allEvents.push(createEventInstance(new Date(current)));
        }
        current.setDate(current.getDate() + 1);
      }
    } else if (frequency === "daily") {
      const endDateLimit = getEndDateLimit(); // ← was always addYears, now fixed
      const current = new Date(eventStartDate);

      while (current <= endDateLimit) {
        allEvents.push(createEventInstance(new Date(current)));
        current.setDate(current.getDate() + 1);
      }
    } else {
      allEvents.push(createEventInstance(eventStartDate));
    }
  }

  return allEvents.sort((a, b) => new Date(a.start) - new Date(b.start));
};

const CalendarEvents = () => {
  const [open, setOpen] = useState(false);
  const [cookies] = useCookies();
  const [events, setEvents] = useState([]);
  const [classSections, setClassSections] = useState([]);
  const [users, setUsers] = useState([]);
  const [academicYears, setAcademicYears] = useState({});
  const [allAcademicYears, setAllAcademicYears] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const roleId = cookies.roleId || "";
  const [showToastMessage, setShowToastMessage] = useState(false);
  const { showToast } = useToast();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventModal, setEventModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleEventClick = (info) => {
    const event = info.event;
    const def = event._def;
    const data = def.extendedProps || {};

    const eventTypeMatch = eventTypes.find(
      (et) => et.eventType === data?.eventTypeId,
    );
    const events = eventTypeMatch
      ? { value: eventTypeMatch.eventTypeId, label: eventTypeMatch.title }
      : null;

    const userparticipants = users
      .filter(
        (user) =>
          Array.isArray(data?.participants) &&
          data.participants.includes(user.userId),
      )
      .map((user) => ({
        value: user.userId,
        label: user.name,
      }));

    const eventacademicYears = allAcademicYears
      .filter(
        (ay) =>
          Array.isArray(data?.academicYear) &&
          data.academicYear.includes(ay.academicYearId),
      )
      .map((ay) => ({
        value: ay.academicYearId,
        label: ay.name,
      }));

    const sections = classSections
      .filter(
        (sec) =>
          Array.isArray(data?.classSection) &&
          data.classSection.includes(sec.classSectionId),
      )
      .map((sec) => ({
        value: sec.classSectionId,
        label: sec.title,
      }));

    setSelectedEvent({
      ...data,
      events: events,
      participants: userparticipants,
      academicYears: eventacademicYears,
      classSections: sections,
    });

    setEventModal(true);
  };

  const handleCloseEventInfo = () => {
    setSelectedEvent(null);
    setEventModal(false);
  };

  const handleManageEvent = async (data) => {
    if (roleId !== "organizationCoordinator") return;
    if (selectedEvent) {
      const payload = {
        userId: cookies.userId,
        eventId: selectedEvent.eventId,
        action: "update",
        ...data,
      };
      setFormLoading(true);
      const response = await updateEvent(payload);
      if (response && response.status === "success") {
        const events = transformEvents(response?.data);
        setEvents(events || []);
        setOpen(false);
        setFormLoading(false);
        setSelectedEvent(null);
        showToast("Event Updated Successfully", "success");
      } else {
        showToast(response?.message || "Failed to update event", "error");
        setFormLoading(false);
      }
    } else {
      setFormLoading(true);
      const payload = {
        userId: cookies.userId,
        ...data,
      };
      const response = await createEvent(payload);
      if (response && response.status === "success") {
        const events = transformEvents(response?.data);
        setEvents(events || []);
        setOpen(false);
        setSelectedEvent(null);
        setFormLoading(false);
        showToast("Event Created Successfully", "success");
      } else {
        setFormLoading(false);
        setSelectedEvent(null);
        showToast(response?.message || "Failed to create event", "error");
      }
    }
  };

  const handleToastMessageClose = () => {
    setShowToastMessage(false);
  };

  const handlefetchEvents = async () => {
    setLoading(true);

    const resetState = () => {
      setAcademicYears({});
      setUsers([]);
      setClassSections([]);
      setEvents([]);
      setEventTypes([]);
    };

    try {
      const [
        classSectionsRes,
        usersRes,
        academicYearsRes,
        eventsRes,
        eventTypesRes,
      ] = await Promise.all([
        fetchClassSections({ userId: cookies.userId }),
        getUsers(cookies.email, cookies.roleId, cookies.organizationId),
        fetchAcademicyears({ userId: cookies.userId }),
        fetchEvents({ userId: cookies.userId }),
        fetchEventTypes({ userId: cookies.userId }),
      ]);

      if (classSectionsRes?.status === "success") {
        setAllAcademicYears(academicYearsRes?.data || []);
        const currentAcademicYear =
          academicYearsRes?.data?.find((a) => a.isCurrent) || {};
        setAcademicYears(currentAcademicYear);

        setUsers(
          usersRes?.data?.users?.filter((u) => u.deleted === false) || [],
        );
        setClassSections(
          classSectionsRes?.data?.filter(
            (section) =>
              section.academicYear === currentAcademicYear?.academicYearId,
          ) || [],
        );
        setEventTypes(eventTypesRes?.data || []);
        setEvents(transformEvents(eventsRes?.data) || []);
      } else {
        resetState();
      }
    } catch (error) {
      resetState();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handlefetchEvents();
  }, [cookies]);

  const handleEdit = () => {
    setEventModal(false);
    setOpen(true);
  };

  const handleDelete = () => {
    setEventModal(false);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (roleId !== "organizationCoordinator") return;
    const payload = {
      userId: cookies.userId,
      eventId: selectedEvent.eventId,
      action: "delete",
    };
    setLoading(true);
    const response = await deleteEvent(payload);
    if (response && response.status === "success") {
      setEvents(transformEvents(response?.data) || []);
      setDeleteOpen(false);
      setSelectedEvent(null);
      setLoading(false);
      showToast("Event Deleted Successfully", "success");
    } else {
      setDeleteOpen(false);
      setSelectedEvent(null);
      setLoading(false);
      showToast(response?.message || "Failed to delete event", "error");
    }
  };

  if (loading)
    return (
      <div className="text-center p-15">
        <Spinner animation="border" />
      </div>
    );
  return (
    <Box sx={{ minHeight: "100vh", padding: "30px", width: "100%" }}>
      <ToastMessage
        open={showToastMessage}
        message="Course Marked As Deleted"
        closeTiming={3000}
        handleClose={handleToastMessageClose}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "10px",
          width: "100%",
          marginBottom: "20px",
        }}
      >
        <RefreshButton handleRefresh={handlefetchEvents} />
        {roleId === "organizationCoordinator" && (
          <Button
            onClick={() => setOpen(true)}
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              boxShadow: "none",
              backgroundColor: "rgb(35, 71, 159)",
            }}
          >
            Create Event
          </Button>
        )}
      </Box>
      <EventsForm
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleManageEvent}
        classSections={classSections}
        users={users}
        academicYears={academicYears}
        eventTypes={eventTypes}
        formLoading={formLoading}
        initialData={selectedEvent}
        setInitialData={setSelectedEvent}
      />
      <Calendar events={events} handleEventClick={handleEventClick} />
      <EventInfo
        event={selectedEvent}
        open={eventModal}
        handleClose={handleCloseEventInfo}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        disable={roleId === "organizationCoordinator"}
      />

      <DeleteModalConfirmation
        deleteOpen={deleteOpen}
        setDeleteOpen={setDeleteOpen}
        deleteButtonText="Delete Event"
        onConfirmDelete={handleConfirmDelete}
      />
    </Box>
  );
};

export default CalendarEvents;
